import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowLeft, MessageCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  return same
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialId = params.get("c");
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(initialId || null);
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const nav = useNavigate();
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConvos(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setThread(data);
      await api.post(`/conversations/${id}/read`);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { loadThread(activeId); }, [activeId, loadThread]);

  // poll for new messages every 4s
  useEffect(() => {
    const t = setInterval(() => {
      loadConvos();
      if (activeId) loadThread(activeId);
    }, 4000);
    return () => clearInterval(t);
  }, [activeId, loadConvos, loadThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || !activeId) return;
    setSending(true);
    try {
      await api.post(`/conversations/${activeId}/messages`, { content });
      setInput("");
      await loadThread(activeId);
      await loadConvos();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="overline mb-2">Private messages</div>
      <h1 className="rainbow-text text-3xl sm:text-4xl mb-6">Inbox</h1>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
        {/* Conversation list */}
        <div className={`paper-card p-2 ${activeId ? "hidden md:block" : ""}`} data-testid="messages-list">
          {convos.length === 0 ? (
            <div className="p-6 text-center text-sm" style={{ color: "var(--ink-soft)" }}>
              <MessageCircle size={22} className="mx-auto mb-2 rainbow-text" />
              No conversations yet. Message an author from their public page or a reader from an application.
            </div>
          ) : (
            convos.map((c) => (
              <button
                key={c.id}
                onClick={() => { setActiveId(c.id); }}
                className={`w-full text-left rounded-lg p-3 flex gap-3 items-center btn-press ${activeId === c.id ? "" : "hover:bg-white/5"}`}
                style={activeId === c.id ? { background: "rgba(224,64,251,0.10)", border: "1px solid rgba(224,64,251,0.35)" } : { border: "1px solid transparent" }}
                data-testid={`convo-${c.id}`}
              >
                <img
                  src={c.peer?.photo_url || "https://api.dicebear.com/9.x/thumbs/svg?seed=" + (c.peer?.id || "x")}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  style={{ border: "1px solid var(--border-soft)" }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="truncate text-sm font-bold">{c.peer?.display_name || "Unknown"}</span>
                    <span className="text-xs flex-shrink-0" style={{ color: "var(--ink-muted)" }}>{timeShort(c.last_message_at)}</span>
                  </div>
                  <div className="text-xs truncate" style={{ color: c.unread_count ? "var(--ink)" : "var(--ink-soft)" }}>
                    {c.last_message || <span className="italic" style={{ color: "var(--ink-muted)" }}>No messages yet</span>}
                  </div>
                </div>
                {c.unread_count > 0 && (
                  <span className="rainbow-text text-xs font-bold rounded-full px-2 py-0.5" style={{ background: "rgba(224,64,251,0.15)" }} data-testid={`convo-unread-${c.id}`}>
                    {c.unread_count}
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div className={`paper-card flex flex-col ${activeId ? "" : "hidden md:flex"}`} style={{ minHeight: "60vh" }}>
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "var(--ink-soft)" }}>
              Pick a conversation to start reading.
            </div>
          ) : (
            <>
              <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border-soft)" }}>
                <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setActiveId(null)} data-testid="thread-back-btn"><ArrowLeft size={16} /></Button>
                <img
                  src={thread.peer?.photo_url || "https://api.dicebear.com/9.x/thumbs/svg?seed=" + (thread.peer?.id || "x")}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                  style={{ border: "1px solid var(--border-soft)" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate" data-testid="thread-peer-name">{thread.peer?.display_name}</div>
                  <div className="text-xs capitalize" style={{ color: "var(--ink-muted)" }}>{thread.peer?.role}</div>
                </div>
                {thread.peer?.role === "author" && (
                  <Button variant="outline" size="sm" className="btn-press" onClick={() => nav(`/authors/${thread.peer.id}`)} data-testid="thread-view-author-btn">View profile</Button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages">
                {thread.messages.length === 0 && (
                  <div className="text-center text-sm py-8" style={{ color: "var(--ink-muted)" }}>Say hello — this is a fresh conversation.</div>
                )}
                {thread.messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className="max-w-[75%] rounded-2xl px-4 py-2 text-sm"
                        style={mine
                          ? { background: "linear-gradient(135deg,#6C63FF,#E040FB)", color: "#fff" }
                          : { background: "#1a1a20", color: "var(--ink)", border: "1px solid var(--border-soft)" }}
                        data-testid={`msg-${m.id}`}
                      >
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                        <div className="text-[10px] mt-1 opacity-70">{timeShort(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="p-3 border-t flex gap-2" style={{ borderColor: "var(--border-soft)" }} data-testid="thread-composer">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Write a message…" data-testid="thread-input" />
                <Button type="submit" disabled={sending || !input.trim()} className="btn-press btn-rainbow" data-testid="thread-send-btn">
                  <Send size={14} className="mr-1" /> Send
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
